import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getGreenfieldMetadata,
  getGreenfieldData,
} from '../../services/internal/greenfield-service'
import { GreenfieldRequest } from '../../models/greenfield/greenfieldRequest'
import { apexToast } from 'apex-ui-kit-pcn'
import ReportingConstants from '../../constants/ReportingConstants'
import {
  analyticsService,
  EventCategories,
} from '../../services/analytics-service'
import { GreenfieldColumns } from 'models/greenfield'

export const fetchGreenfieldMetadataAction = createAsyncThunk(
  'greenfield/fetchGreenfieldMetadata',
  async (cardId: any) => {
    const response = await getGreenfieldMetadata(cardId)
    return response.data
  },
)

/**
 * Retrieve row-level Greenfield data. Uses the card id from the currently
 * loaded metadata.
 */
export const fetchGreenfieldDataAction = createAsyncThunk(
  'greenfield/fetchGreenfieldData',
  async (data: { cardId: string; request: GreenfieldRequest }) => {
    const { cardId, request } = data
    return await getGreenfieldData(cardId, request)
  },
)

const initialState = {
  greenfieldViewerFilters: [] as any[],
  greenfieldColumns: null as GreenfieldColumns[] | null,
  greenfieldData: [] as any[],
  greenfieldTotalsData: [] as any[],
  isFetchingGreenfieldMetadata: false,
  isFetchingGreenfieldData: false,
  currentGreenfieldRequest: null as GreenfieldRequest | null,
  fieldToRefIdMap: {},
}

const greenfieldSlice = createSlice({
  name: 'greenfield',
  initialState,
  reducers: {
    resetGreenfieldAction: state => {
      state.greenfieldViewerFilters = []
      state.greenfieldColumns = null
      state.greenfieldData = []
      state.greenfieldTotalsData = []
    },
  },
  extraReducers: builder => {
    builder

      // fetchGreenfieldMetadataAction
      .addCase(fetchGreenfieldMetadataAction.pending, state => {
        state.isFetchingGreenfieldMetadata = true
      })
      .addCase(fetchGreenfieldMetadataAction.fulfilled, (state, action) => {
        const { payload } = action
        return {
          ...state,
          greenfieldViewerFilters: payload.card_query_attribute.viewer_filters,
          greenfieldColumns: payload.card_query_attribute.columns.filter(
            //These fields are derived in the grid as we need weighted averages
            col =>
              col.field_name !== 'pmu_a' &&
              col.field_name !== 'net_pmu_a' &&
              col.field_name !== 'initial_markup_a',
          ),
          fieldToRefIdMap: payload.card_query_attribute.columns.reduce(
            (acc, col) => {
              acc[col.field_name] = col.ref_id
              return acc
            },
            {},
          ),
          isFetchingGreenfieldMetadata: false,
        }
      })
      .addCase(fetchGreenfieldMetadataAction.rejected, state => {
        state.isFetchingGreenfieldMetadata = false
        state.greenfieldColumns = null
        state.fieldToRefIdMap = {}
        state.greenfieldViewerFilters = []
      })

      // fetchGreenfieldDataAction
      .addCase(fetchGreenfieldDataAction.pending, state => {
        state.isFetchingGreenfieldData = true
      })
      .addCase(fetchGreenfieldDataAction.fulfilled, (state, action) => {
        const { payload } = action
        const { request } = action.meta.arg

        if (payload.data.message) {
          analyticsService.event({
            category: EventCategories.OperationalReportingPage,
            action: 'Fetching Report',
            label: 'Failure: More than 30k rows returned.',
          })
          apexToast.error({
            message: [ReportingConstants.TOO_MANY_ROWS_MESSAGE],
            showDismissButton: true,
            autoHideDuration: null,
          })

          state.isFetchingGreenfieldData = false
          state.greenfieldData = []
          state.currentGreenfieldRequest = null
        } else {
          state.greenfieldData = payload.data.query_results
          state.greenfieldTotalsData = payload?.data?.total_results?.[0]
          state.isFetchingGreenfieldData = false
          state.currentGreenfieldRequest = request
        }
      })
      .addCase(fetchGreenfieldDataAction.rejected, state => {
        state.isFetchingGreenfieldData = false
      })
  },
})

export const { resetGreenfieldAction } = greenfieldSlice.actions

export default greenfieldSlice.reducer
